import React, { forwardRef, useEffect, useRef } from "react";
import Cropper from "./cropperjs/src";
import "./cropperjs/src/css/cropper.css";
import { CustomCropper } from "./custom-cropper";
import { assign } from "./cropperjs/src/js/utilities";

const REQUIRED_IMAGE_STYLES = {opacity: 0, maxWidth: '100%'};

const useCombinedRefs = (...refs) => {
  const targetRef = useRef(null);

  useEffect(() => {
      refs.forEach((ref) => {
          if (!ref) return;

          if (typeof ref === 'function') {
              ref(targetRef.current);
          } else {
              ref.current = targetRef.current;
          }
      });
  }, [refs]);

  return targetRef;
};

const applyDefaultOptions = (cropper, options) => {
  const {enable = true, scaleX = 1, scaleY = 1, zoomTo = 0, rotateTo} = options;
  enable ? cropper.enable() : cropper.disable();
  cropper.scaleX(scaleX);
  cropper.scaleY(scaleY);
  rotateTo !== undefined && cropper.rotateTo(rotateTo);
  zoomTo > 0 && cropper.zoomTo(zoomTo);
};

const cropperProps = [
  'aspectRatio',
  'autoCrop',
  'autoCropArea',
  'background',
  'center',
  'checkCrossOrigin',
  'checkOrientation',
  'cropBoxMovable',
  'cropBoxResizable',
  'data',
  'dragMode',
  'guides',
  'highlight',
  'initialAspectRatio',
  'minCanvasHeight',
  'minCanvasWidth',
  'minContainerHeight',
  'minContainerWidth',
  'minCropBoxHeight',
  'minCropBoxWidth',
  'modal',
  'movable',
  'preview',
  'responsive',
  'restore',
  'rotatable',
  'scalable',
  'toggleDragModeOnDblclick',
  'viewMode',
  'wheelZoomRatio',
  'zoomOnTouch',
  'zoomOnWheel',
  'zoomable',
  'cropstart',
  'cropmove',
  'cropend',
  'crop',
  'zoom',
  'ready',
  
  // custom props
  'onReplace',
  'zoomed',
  'wheel',
  'wheelend',
];

const cleanImageProps = (imageProps) =>
  cropperProps.reduce((acc, key) => {
      const {[key]: _, ...rest} = acc;
      return rest;
  }, imageProps);

const ReactCropper = React.memo(forwardRef(({ ...props }, ref) => {
  const {
    dragMode = "crop",
    src,
    style,
    className,
    crossOrigin,
    scaleX,
    scaleY,
    enable,
    zoomTo,
    rotateTo,
    alt = "picture",
    ready,
    onInitialized,
    ...rest
  } = props;
  const defaultOptions = { scaleY, scaleX, enable, zoomTo, rotateTo };
  const innerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, innerRef);

  const resetComponent = () => {
    if (combinedRef.current?.cropper) {
      combinedRef.current.cropper.reset();
      combinedRef.current.cropper.clear();
      if (typeof src !== "undefined") {
        props?.onReplace();
        combinedRef.current.cropper.replace(src);
      }
    }
  };
  
  /**
   * re-render when src changes
   */
  useEffect(() => {
    if (combinedRef.current?.cropper && typeof src !== "undefined") {
      if (props.onReplace && typeof props.onReplace === 'function') {
        props?.onReplace();
      }
      combinedRef.current.cropper.reset().clear().replace(src);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // console.log('<ReactCropper />');

  useEffect(() => {
    let cropper;

    if (combinedRef.current !== null) {
      
      /* -------------- custom cropper methods -------------- */
      assign(Cropper.prototype, CustomCropper);

      cropper = new Cropper(combinedRef.current, {
        dragMode,
        ...rest,
        ready: (e) => {
          if (e.currentTarget !== null) {
            applyDefaultOptions(e.currentTarget.cropper, defaultOptions);
          }
          ready && ready(e);
        },
      });
      cropper.zero = resetComponent;
      onInitialized && onInitialized(cropper);

      // window.cropper = cropper;

      /**
       * destroy cropper on un-mount
       */
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        cropper && cropper.destroy();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const imageProps = cleanImageProps({ ...rest, crossOrigin, src, alt });

  return (
    <div style={style} className={className}>
      <img {...imageProps} style={REQUIRED_IMAGE_STYLES} alt="" ref={combinedRef} />
    </div>
  );
}));

export default ReactCropper;